import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Lock } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const ROLES = [
  ["Steering group", "Scope, budget, participating agencies, publication, expansion.", "Charter, minutes, conflict-of-interest disclosures."],
  ["Medical director / clinical panel", "Clinical crosswalk, scope boundaries, assessment thresholds, adverse-event review.", "U.S. credentials, signed approvals, version history."],
  ["Agency operations leads", "Local policy, command, exercise safety, staffing, mutual-aid integration.", "Agency appointment letters, approved SOP and MOU."],
  ["Independent evaluator", "Evaluation design, scoring audit, analysis, statement of limitations, final report.", "Statement of independence, protocol, analysis files."],
  ["Data governance lead", "Data minimization, access, retention, sharing, incident response.", "Data-use agreement, data inventory, audit log."],
  ["Program proponent", "Architecture, curriculum integration, training operations, documentation, corrective-action management.", "Defined scope, deliverables, progress evidence, partner attestations."],
];

const PRIVACY = [
  "Collect only fields tied to a defined measure; prohibit secondary use without governance approval.",
  "Separate participant identity from performance data and restrict access by role.",
  "Encrypt in transit and at rest; log access; define retention and deletion schedules.",
  "Complete a privacy, security, and legal review before connecting to any operational system.",
  "Publish only de-identified aggregate results, with small-cell suppression where required.",
];

export default function Governance() {
  return (
    <main data-testid="governance-page">
      <PageHead
        chapter="07"
        eyebrow="Governance and Safety"
        titleLines={["No party approves", "its own work."]}
        lede="The pilot distributes decision rights so that no one party — including the program itself — can approve its own work."
      />

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="decision-rights-section">
        <Reveal>
          <Eyebrow>Steering group and decision rights</Eyebrow>
          <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            Six roles, distributed authority.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 overflow-x-auto">
            <table className="tep-table">
              <thead>
                <tr><th>Role</th><th>Decision right</th><th>Minimum documentation</th></tr>
              </thead>
              <tbody>
                {ROLES.map((r, i) => (
                  <tr key={i} data-testid={`governance-role-${i}`}>
                    <td className="font-display font-bold text-white whitespace-nowrap">{r[0]}</td>
                    <td>{r[1]}</td>
                    <td>{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 bg-surface border border-signal/40 p-8 flex items-start gap-5 max-w-3xl">
            <Lock size={20} className="text-signal shrink-0 mt-1" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">Separation rule</p>
              <p className="text-slate-200 leading-relaxed font-display font-bold text-lg tracking-tight">
                The program proponent holds no clinical approval right, no command authority, and no control over the evaluation or its findings.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-line bg-surface py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>Privacy and cybersecurity</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
              Minimize first. Simulate first.
            </h2>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed">
              The pilot minimizes patient data and relies first on simulation and aggregate readiness records. If protected health information is ever used, covered entities and business associates must determine the applicable HIPAA pathway, contracts, and security controls. Cybersecurity governance is aligned to the NIST Cybersecurity Framework 2.0 — Govern, Identify, Protect, Detect, Respond, Recover.
            </p>
            <ul className="mt-8 space-y-4">
              {PRIVACY.map((p, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                  <span className="text-signal font-mono text-xs mt-0.5">→</span> {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Quality assurance</Eyebrow>
            <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
              Distributed training fails predictably. QA is embedded.
            </h2>
            <p className="mt-6 text-sm text-slate-400 leading-relaxed">
              Instruction drifts, evaluators score differently, and sites optimize for the metric instead of the capability. T-EMRP embeds instructor observation, scenario fidelity checks, a dual-scored sample, data-quality monitoring, version control, and corrective coaching. A site that departs from the standard is supported back to fidelity and retested — not quietly excluded from the results.
            </p>
            <div className="mt-8 relative overflow-hidden border border-line">
              <img src={IMAGES.simulation} alt="Quality assurance in simulation training" className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700" />
              <span className="absolute bottom-0 left-0 bg-ink/80 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 px-4 py-2">
                Fidelity checks · Dual scoring
              </span>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="governance-cta-contact" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Ask a Governance Question <ArrowUpRight size={15} />
              </Link>
              <Link to="/research" data-testid="governance-cta-research" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Evaluation Framework <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
